<template>
  <div>
    <div class="heading-container">
      <ui-heading tag="h3" no-top-margin>
        <span class="highlighted">{{ $t("identity_data_heading") }}</span>
      </ui-heading>
      <span v-if="dataSource" class="data-source">
        {{ $t("identity_data.data_source", { source: dataSource }) }}
      </span>
    </div>
    <ui-field
      v-for="{ key, label, value, highlighted } in fields"
      :key="key"
      :class="{ 'highlighted-label': highlighted }"
      label-position="inline"
      :label="label"
      bottom-margin="small"
    >
      {{ value }}
    </ui-field>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { getters } from "@/store";
import { DOCUMENT_FIELDS } from "@/constants";
import { documentDateFormat } from "@/utils/date";

export default {
  computed: {
    ...mapGetters([getters.currentDocumentData]),
    dataSource() {
      return this.currentDocumentData?.electronicIdentityProvider;
    },
    fields() {
      const document = this.currentDocumentData;
      if (!document) {
        return [];
      }

      return [
        {
          key: "providerType",
          label: this.$t("identity_data.provider_type"),
          value: this.translate("provider_type", document.providerType),
          highlighted: true
        },
        {
          key: DOCUMENT_FIELDS.DOC_TYPE,
          label: this.$t("document_form.doc_type"),
          value: this.translate(
            "document_type",
            document[DOCUMENT_FIELDS.DOC_TYPE]
          )
        },
        {
          key: DOCUMENT_FIELDS.FIRST_NAME,
          label: this.$t("document_form.first_name"),
          value: document[DOCUMENT_FIELDS.FIRST_NAME]
        },
        {
          key: DOCUMENT_FIELDS.LAST_NAME,
          label: this.$t("document_form.last_name"),
          value: document[DOCUMENT_FIELDS.LAST_NAME]
        },
        {
          key: DOCUMENT_FIELDS.DATE_OF_BIRTH,
          label: this.$t("document_form.date_of_birth"),
          value: this.formatDate(document[DOCUMENT_FIELDS.DATE_OF_BIRTH])
        },
        {
          key: DOCUMENT_FIELDS.NATIONALITY,
          label: this.$t("document_form.nationality"),
          value: this.translate(
            "countries",
            document[DOCUMENT_FIELDS.NATIONALITY]
          )
        },
        {
          key: DOCUMENT_FIELDS.PERSONAL_CODE,
          label: this.$t("document_form.personal_code"),
          value: document[DOCUMENT_FIELDS.PERSONAL_CODE]
        },
        {
          key: DOCUMENT_FIELDS.GENDER,
          label: this.$t("document_form.gender"),
          value: this.translate("gender", document[DOCUMENT_FIELDS.GENDER])
        },
        {
          key: DOCUMENT_FIELDS.DOC_NUMBER,
          label: this.$t("document_form.doc_number"),
          value: document[DOCUMENT_FIELDS.DOC_NUMBER]
        },
        {
          key: DOCUMENT_FIELDS.DATE_OF_ISSUE,
          label: this.$t("document_form.date_of_issue"),
          value: this.formatDate(document[DOCUMENT_FIELDS.DATE_OF_ISSUE])
        },
        {
          key: DOCUMENT_FIELDS.DATE_OF_EXPIRATION,
          label: this.$t("document_form.date_of_expiration"),
          value: this.formatDate(document[DOCUMENT_FIELDS.DATE_OF_EXPIRATION])
        },
        {
          key: DOCUMENT_FIELDS.PLACE_OF_BIRTH,
          label: this.$t("document_form.place_of_birth"),
          value: document[DOCUMENT_FIELDS.PLACE_OF_BIRTH]
        },
        {
          key: DOCUMENT_FIELDS.COUNTRY,
          label: this.$t("document_form.country"),
          value: this.translate("countries", document[DOCUMENT_FIELDS.COUNTRY])
        },
        {
          key: DOCUMENT_FIELDS.STATE,
          label: this.$t("document_form.state"),
          value: document[DOCUMENT_FIELDS.STATE]
        },
        {
          key: DOCUMENT_FIELDS.ADDRESS,
          label: this.$t("document_form.address"),
          value: document[DOCUMENT_FIELDS.ADDRESS]
        },
        {
          key: DOCUMENT_FIELDS.CATEGORY,
          label: this.$t("document_form.category"),
          value: document[DOCUMENT_FIELDS.CATEGORY]
        }
      ].filter(({ value }) => !!value);
    }
  },
  methods: {
    translate(prefix, value) {
      if (!value) {
        return null;
      }
      const key = `${prefix}.${value}`;
      const isTranslated =
        this.$te(key) || this.$te(key, this.$i18n.fallbackLocale);
      return isTranslated ? this.$t(key) : value;
    },

    formatDate(isoDate) {
      return isoDate && documentDateFormat(new Date(`${isoDate}T00:00`), false);
    }
  }
};
</script>

<style scoped lang="scss">
.heading-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.highlighted {
  color: var(--black);
  background-color: var(--amber);
}
.highlighted-label ::v-deep .field-label {
  color: var(--black);
  background-color: var(--amber);
  background-clip: content-box;
}
.data-source {
  padding: 0.25rem 0.5rem;
  font-size: 1rem;
  font-weight: 500;
  line-height: var(--input-line-height);
  color: var(--badge-success-text-color);
  background: var(--badge-success-background);
  border-radius: 1.5rem;
}
</style>
